import type {
  ApiCalendar,
  ApiCalendarEntry,
  ApiDate,
  ApiTime,
  CreateEventInput,
  UpdateEventInput,
  CreateCalendarInput,
  UpdateCalendarInput,
  AuthStatus,
} from "./types";

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown,
  ) {
    super(message);
  }
}

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "X-Requested-With": "XMLHttpRequest",
    ...(options?.headers as Record<string, string>),
  };

  if (options?.body && typeof options.body === "string") {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(url, {
    ...options,
    headers,
    credentials: "same-origin",
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new ApiError(res.status, data?.error ?? res.statusText, data);
  }

  const contentType = res.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    return res.json();
  }
  return {} as T;
}

// --- Auth ---

export async function getAuthStatus(): Promise<AuthStatus> {
  return apiFetch("/api/auth/status");
}

export async function setup(password: string): Promise<void> {
  await apiFetch("/api/auth/setup", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export async function login(password: string, totp?: string): Promise<{ totpRequired?: boolean }> {
  try {
    await apiFetch("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ password, totp }),
    });
    return {};
  } catch (e) {
    if (e instanceof ApiError && e.data && typeof e.data === "object" && "totpRequired" in e.data) {
      return { totpRequired: true };
    }
    throw e;
  }
}

export async function logout(): Promise<void> {
  await apiFetch("/api/auth/logout", { method: "POST" });
}

// --- Calendars ---

export async function fetchCalendars(): Promise<ApiCalendar[]> {
  return apiFetch("/api/calendars");
}

export async function createCalendarApi(input: CreateCalendarInput): Promise<ApiCalendar> {
  return apiFetch("/api/calendars", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateCalendarApi(
  id: string,
  input: UpdateCalendarInput,
): Promise<ApiCalendar> {
  return apiFetch(`/api/calendars/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function deleteCalendarApi(id: string): Promise<void> {
  await apiFetch(`/api/calendars/${id}`, { method: "DELETE" });
}

// --- Events (scoped under calendars) ---

export async function fetchEvents(
  calendarId: string,
  start: string,
  end: string,
): Promise<ApiCalendarEntry[]> {
  return apiFetch(
    `/api/calendars/${calendarId}/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
  );
}

export async function createEventApi(
  calendarId: string,
  input: CreateEventInput,
): Promise<ApiCalendarEntry> {
  return apiFetch(`/api/calendars/${calendarId}/events`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateEventApi(
  calendarId: string,
  eventId: string,
  input: UpdateEventInput,
): Promise<ApiCalendarEntry> {
  return apiFetch(`/api/calendars/${calendarId}/events/${eventId}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function deleteEventApi(calendarId: string, eventId: string): Promise<void> {
  await apiFetch(`/api/calendars/${calendarId}/events/${eventId}`, { method: "DELETE" });
}

// --- Search ---

export interface SearchEventResult {
  id: string;
  calendarId: string;
  calendarName: string;
  calendarColor: string;
  heading: string;
  content: string | null;
  color: string;
  date: {
    start: ApiDate;
    end: ApiDate;
  };
  time: {
    start: ApiTime;
    end: ApiTime;
  } | null;
}

export async function searchEvents(q: string, signal?: AbortSignal): Promise<SearchEventResult[]> {
  const data = await apiFetch<{ results: SearchEventResult[] }>(
    `/api/events/search?q=${encodeURIComponent(q)}`,
    { signal },
  );
  return data.results;
}

// --- Reminders ---

export interface UpcomingReminderRow {
  id: string;
  calendarId: string;
  calendarName: string;
  heading: string;
  startYear: number;
  startMonth: number;
  startDay: number;
  startHour: number | null;
  startMinute: number | null;
  reminderMinutesBefore: number;
}

export async function fetchUpcomingReminders(
  days?: number,
  signal?: AbortSignal,
): Promise<UpcomingReminderRow[]> {
  const qs = days ? `?days=${encodeURIComponent(days)}` : "";
  const data = await apiFetch<{ reminders: UpcomingReminderRow[] }>(
    `/api/events/reminders${qs}`,
    { signal },
  );
  return data.reminders;
}

// --- Settings ---

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await apiFetch("/api/password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function get2FASetup(): Promise<{ enabled: boolean; secret?: string; uri?: string }> {
  return apiFetch("/api/2fa/setup");
}

export async function verify2FA(code: string): Promise<void> {
  await apiFetch("/api/2fa/verify", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export async function disable2FA(password: string): Promise<void> {
  await apiFetch("/api/2fa/disable", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}
